import { config } from './config';

export enum FeatureFlagId {
  EnableDiagnosticMode = 'enableDiagnosticMode',
  EnableNewConvergeBrand = 'enableNewConvergeBrand',
  EnableRiskProfileAttackSurfaceModals = 'enableRiskProfileAttackSurfaceModals',
  EnableSearchV1 = 'enableSearchV1',
  HideEnvironmentIndicator = 'hideEnvironmentIndicator',
  IncludeNonSubmissionResultsInSearch = 'includeNonSubmissionResultsInSearch',
  ShowClearanceSectionOnSubmissionOverview = 'showClearanceSectionOnSubmissionOverview',
  ShowClearedAtColumnOnUnderwritingQueue = 'showClearedAtColumnOnUnderwritingQueue',
  ShowDuplicateQuoteButton = 'showDuplicateQuoteButton',
  ShowQuestionsNotApplicableMessage = 'showQuestionsNotApplicableMessage',
  ShowRaterUpdateNotifications = 'showRaterUpdateNotifications',
  MockEndorsementQuestions = 'mockEndorsementQuestions',
  ShowRiskProfileAttackSurface = 'showRiskProfileAttackSurface',
  ShowRiskProfileAttackSurfaceRiskLevels = 'showRiskProfileAttackSurfaceRiskLevels',
  ShowRiskProfileCreateQuoteOptionButton = 'showRiskProfileCreateQuoteOptionButton',
  ShowRiskProfileRequestInfoButton = 'showRiskProfileRequestInfoButton',
  ShowRiskProfileRiskReportButton = 'showRiskProfileRiskReportButton',
  ShowSortQuoteOptionsButton = 'showSortQuoteOptionsButton',
  ShowUpdateSubmissionStatusButtonOnQuotePage = 'showUpdateSubmissionStatusButtonOnQuotePage',
  EnableAddConvergeToExcessTowerPrompt = 'enableAddConvergeToExcessTowerPrompt',
  EnableRaterStateIndicator = 'enableRaterStateIndicator',
  EnableRaterExcessLayerParticipantReplacement = 'enableRaterExcessLayerParticipantReplacement',
  EnableRaterQueryValidation = 'enableRaterQueryValidation',
  EnableRaterMessageValidation = 'enableRaterMessageValidation',
  EnablePartialRaterSaves = 'enablePartialRaterSaves',
}

const isBoolEnvVar = (
  value: string | undefined
): boolean | null | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value
    .toLowerCase()
    .replace('true', 't')
    .replace('1', 't')
    .replace('false', 'f')
    .replace('0', 'f');

  if (normalized === 't') {
    return true;
  }

  if (normalized === 'f') {
    return false;
  }

  if (normalized === 'null') {
    return null;
  }

  return undefined;
};

export const FEATURE_FLAG_ENV_VARS: Record<
  FeatureFlagId,
  {
    defaultValue?: boolean | null;
    overrideValue?: boolean | null;
  }
> = {
  [FeatureFlagId.EnableDiagnosticMode]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableDiagnosticMode']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableDiagnosticMode']
    ),
  },
  [FeatureFlagId.EnableNewConvergeBrand]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableNewConvergeBrand']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableNewConvergeBrand']
    ),
  },
  [FeatureFlagId.EnableRiskProfileAttackSurfaceModals]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableRiskProfileAttackSurfaceModals']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_enableRiskProfileAttackSurfaceModals'
      ]
    ),
  },
  [FeatureFlagId.EnableSearchV1]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableSearchV1']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableSearchV1']
    ),
  },
  [FeatureFlagId.HideEnvironmentIndicator]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_hideEnvironmentIndicator']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_hideEnvironmentIndicator']
    ),
  },
  [FeatureFlagId.IncludeNonSubmissionResultsInSearch]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_includeNonSubmissionResultsInSearch']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_includeNonSubmissionResultsInSearch'
      ]
    ),
  },
  [FeatureFlagId.ShowClearanceSectionOnSubmissionOverview]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_showClearanceSectionOnSubmissionOverview'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showClearanceSectionOnSubmissionOverview'
      ]
    ),
  },
  [FeatureFlagId.ShowClearedAtColumnOnUnderwritingQueue]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_showClearedAtColumnOnUnderwritingQueue'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showClearedAtColumnOnUnderwritingQueue'
      ]
    ),
  },
  [FeatureFlagId.ShowDuplicateQuoteButton]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showDuplicateQuoteButton']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_showDuplicateQuoteButton']
    ),
  },
  [FeatureFlagId.ShowQuestionsNotApplicableMessage]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showQuestionsNotApplicableMessage']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showQuestionsNotApplicableMessage'
      ]
    ),
  },
  [FeatureFlagId.ShowRaterUpdateNotifications]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showRaterUpdateNotifications']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_showRaterUpdateNotifications']
    ),
  },
  [FeatureFlagId.MockEndorsementQuestions]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_mockEndorsementQuestions']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_mockEndorsementQuestions']
    ),
  },
  [FeatureFlagId.ShowRiskProfileAttackSurface]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showRiskProfileAttackSurface']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_showRiskProfileAttackSurface']
    ),
  },
  [FeatureFlagId.ShowRiskProfileAttackSurfaceRiskLevels]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_showRiskProfileAttackSurfaceRiskLevels'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showRiskProfileAttackSurfaceRiskLevels'
      ]
    ),
  },
  [FeatureFlagId.ShowRiskProfileCreateQuoteOptionButton]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_showRiskProfileCreateQuoteOptionButton'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showRiskProfileCreateQuoteOptionButton'
      ]
    ),
  },
  [FeatureFlagId.ShowRiskProfileRequestInfoButton]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showRiskProfileRequestInfoButton']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showRiskProfileRequestInfoButton'
      ]
    ),
  },
  [FeatureFlagId.ShowRiskProfileRiskReportButton]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showRiskProfileRiskReportButton']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showRiskProfileRiskReportButton'
      ]
    ),
  },
  [FeatureFlagId.ShowSortQuoteOptionsButton]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_showSortQuoteOptionsButton']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_showSortQuoteOptionsButton']
    ),
  },
  [FeatureFlagId.ShowUpdateSubmissionStatusButtonOnQuotePage]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_showUpdateSubmissionStatusButtonOnQuotePage'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_showUpdateSubmissionStatusButtonOnQuotePage'
      ]
    ),
  },
  [FeatureFlagId.EnableAddConvergeToExcessTowerPrompt]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableAddConvergeToExcessTowerPrompt']
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_enableAddConvergeToExcessTowerPrompt'
      ]
    ),
  },
  [FeatureFlagId.EnableRaterStateIndicator]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableRaterStateIndicator']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableRaterStateIndicator']
    ),
  },
  [FeatureFlagId.EnableRaterExcessLayerParticipantReplacement]: {
    overrideValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_enableRaterExcessLayerParticipantReplacement'
      ]
    ),
    defaultValue: isBoolEnvVar(
      process.env[
        'REACT_APP_FEATURE_FLAG_DEFAULT_enableRaterExcessLayerParticipantReplacement'
      ]
    ),
  },
  [FeatureFlagId.EnableRaterQueryValidation]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableRaterQueryValidation']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableRaterQueryValidation']
    ),
  },
  [FeatureFlagId.EnableRaterMessageValidation]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enableRaterMessageValidation']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enableRaterMessageValidation']
    ),
  },
  [FeatureFlagId.EnablePartialRaterSaves]: {
    overrideValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_enablePartialRaterSaves']
    ),
    defaultValue: isBoolEnvVar(
      process.env['REACT_APP_FEATURE_FLAG_DEFAULT_enablePartialRaterSaves']
    ),
  },
};

const OVERRIDE_VALUES = Object.values(FeatureFlagId)
  .filter(
    (key) => typeof FEATURE_FLAG_ENV_VARS[key].overrideValue === 'boolean'
  )
  .reduce<Record<FeatureFlagId, boolean>>(
    (acc, key) => ({
      ...acc,
      [key]: FEATURE_FLAG_ENV_VARS[key].overrideValue,
    }),
    {} as Record<FeatureFlagId, boolean>
  );

const DEFAULT_VALUES = Object.values(FeatureFlagId)
  .filter((key) => typeof FEATURE_FLAG_ENV_VARS[key].defaultValue === 'boolean')
  .reduce<Record<FeatureFlagId, boolean>>(
    (acc, key) => ({
      ...acc,
      [key]: FEATURE_FLAG_ENV_VARS[key].defaultValue,
    }),
    {} as Record<FeatureFlagId, boolean>
  );

export const FEATURE_FLAG_LABELS: Record<FeatureFlagId, string> = {
  [FeatureFlagId.ShowClearanceSectionOnSubmissionOverview]:
    'Show clearance section on submission overview',
  [FeatureFlagId.ShowDuplicateQuoteButton]: 'Show duplicate quote button',
  [FeatureFlagId.ShowUpdateSubmissionStatusButtonOnQuotePage]:
    'Show update submission status button on quote page',
  [FeatureFlagId.ShowQuestionsNotApplicableMessage]:
    'Show the count of questions that are not applicable',
  [FeatureFlagId.ShowRiskProfileRequestInfoButton]:
    'Show risk profile request info button',
  [FeatureFlagId.ShowRiskProfileCreateQuoteOptionButton]:
    'Show risk profile create quote option button',
  [FeatureFlagId.ShowSortQuoteOptionsButton]: 'Show sort quote options button',
  [FeatureFlagId.EnableDiagnosticMode]: 'Enable diagnostic mode',
  [FeatureFlagId.ShowRiskProfileAttackSurface]:
    'Show risk profile attack surface',
  [FeatureFlagId.EnableRiskProfileAttackSurfaceModals]:
    'Enable risk profile attack surface modals',
  [FeatureFlagId.ShowRiskProfileRiskReportButton]:
    'Show risk profile risk report button',
  [FeatureFlagId.ShowRiskProfileAttackSurfaceRiskLevels]:
    'Show risk profile attack surface risk levels',
  [FeatureFlagId.HideEnvironmentIndicator]: 'Hide environment indicator',
  [FeatureFlagId.ShowClearedAtColumnOnUnderwritingQueue]:
    'Show cleared date on Submissions',
  [FeatureFlagId.EnableNewConvergeBrand]: 'Enable new Converge brand',
  [FeatureFlagId.EnableSearchV1]: 'Use search V1',
  [FeatureFlagId.IncludeNonSubmissionResultsInSearch]:
    'Include non-submission results in search',
  [FeatureFlagId.ShowRaterUpdateNotifications]:
    'Show rater update notifications',
  [FeatureFlagId.EnableRaterStateIndicator]: 'Enable rater state indicator',
  [FeatureFlagId.EnableAddConvergeToExcessTowerPrompt]:
    'Enable "add converge to tower" excess prompt',
  [FeatureFlagId.MockEndorsementQuestions]: 'Mock endorsement questions',
  [FeatureFlagId.EnableRaterExcessLayerParticipantReplacement]:
    'Enable rater excess layer participant replacement',
  [FeatureFlagId.EnableRaterQueryValidation]: 'Enable rater query validation',
  [FeatureFlagId.EnableRaterMessageValidation]:
    'Enable rater message validation',
  [FeatureFlagId.EnablePartialRaterSaves]: 'Enable partial rater saves',
};

export const getFeatureFlagLabel = (key: FeatureFlagId) =>
  FEATURE_FLAG_LABELS[key];

export class FeatureFlags implements Record<FeatureFlagId, boolean> {
  asJSON(): Record<FeatureFlagId, boolean> {
    return Object.values(FeatureFlagId).reduce<Record<FeatureFlagId, boolean>>(
      (acc, key) => ({
        ...acc,
        [key]: this.get(key),
      }),
      {} as Record<FeatureFlagId, boolean>
    );
  }

  get overrideValues() {
    return OVERRIDE_VALUES;
  }

  get defaultValues() {
    return DEFAULT_VALUES;
  }

  get state() {
    return this.asJSON();
  }

  getFeatureFlagKey(key: FeatureFlagId) {
    return `featureFlag:${key}` as const;
  }

  set(key: FeatureFlagId, value: boolean) {
    const { overrideValue } = FEATURE_FLAG_ENV_VARS[key];

    localStorage.setItem(
      this.getFeatureFlagKey(key),
      `${overrideValue ?? value}`
    );
  }

  get(key: FeatureFlagId): boolean {
    const { defaultValue, overrideValue } = FEATURE_FLAG_ENV_VARS[key];

    const storedValue = localStorage.getItem(this.getFeatureFlagKey(key));

    const enabled =
      overrideValue ??
      (storedValue === 'true'
        ? true
        : storedValue === 'false'
        ? false
        : defaultValue ?? false);

    if (
      enabled &&
      typeof overrideValue !== 'boolean' &&
      !config.enableFeatureFlags
    ) {
      this.set(key, false);
      return false;
    }

    return enabled;
  }

  get showClearanceSectionOnSubmissionOverview() {
    return this.get(FeatureFlagId.ShowClearanceSectionOnSubmissionOverview);
  }

  set showClearanceSectionOnSubmissionOverview(value: boolean) {
    this.set(FeatureFlagId.ShowClearanceSectionOnSubmissionOverview, value);
  }

  get showDuplicateQuoteButton() {
    return this.get(FeatureFlagId.ShowDuplicateQuoteButton);
  }

  set showDuplicateQuoteButton(value: boolean) {
    this.set(FeatureFlagId.ShowDuplicateQuoteButton, value);
  }

  get showUpdateSubmissionStatusButtonOnQuotePage() {
    return this.get(FeatureFlagId.ShowUpdateSubmissionStatusButtonOnQuotePage);
  }

  set showUpdateSubmissionStatusButtonOnQuotePage(value: boolean) {
    this.set(FeatureFlagId.ShowUpdateSubmissionStatusButtonOnQuotePage, value);
  }

  get showQuestionsNotApplicableMessage() {
    return this.get(FeatureFlagId.ShowQuestionsNotApplicableMessage);
  }

  set showQuestionsNotApplicableMessage(value: boolean) {
    this.set(FeatureFlagId.ShowQuestionsNotApplicableMessage, value);
  }

  get showRiskProfileRequestInfoButton() {
    return this.get(FeatureFlagId.ShowRiskProfileRequestInfoButton);
  }

  set showRiskProfileRequestInfoButton(value: boolean) {
    this.set(FeatureFlagId.ShowRiskProfileRequestInfoButton, value);
  }

  get showRiskProfileCreateQuoteOptionButton() {
    return this.get(FeatureFlagId.ShowRiskProfileRequestInfoButton);
  }

  set showRiskProfileCreateQuoteOptionButton(value: boolean) {
    this.set(FeatureFlagId.ShowRiskProfileRequestInfoButton, value);
  }

  get showSortQuoteOptionsButton() {
    return this.get(FeatureFlagId.ShowSortQuoteOptionsButton);
  }

  set showSortQuoteOptionsButton(value: boolean) {
    this.set(FeatureFlagId.ShowSortQuoteOptionsButton, value);
  }

  get enableDiagnosticMode() {
    return this.get(FeatureFlagId.EnableDiagnosticMode);
  }

  set enableDiagnosticMode(value: boolean) {
    this.set(FeatureFlagId.EnableDiagnosticMode, value);
  }

  get showRiskProfileAttackSurface() {
    return this.get(FeatureFlagId.ShowRiskProfileAttackSurface);
  }

  set showRiskProfileAttackSurface(value: boolean) {
    this.set(FeatureFlagId.ShowRiskProfileAttackSurface, value);
  }

  get enableRiskProfileAttackSurfaceModals() {
    return this.get(FeatureFlagId.EnableRiskProfileAttackSurfaceModals);
  }

  set enableRiskProfileAttackSurfaceModals(value: boolean) {
    this.set(FeatureFlagId.EnableRiskProfileAttackSurfaceModals, value);
  }

  get showRiskProfileRiskReportButton() {
    return this.get(FeatureFlagId.ShowRiskProfileRiskReportButton);
  }

  set showRiskProfileRiskReportButton(value: boolean) {
    this.set(FeatureFlagId.ShowRiskProfileRiskReportButton, value);
  }

  get showRiskProfileAttackSurfaceRiskLevels() {
    return this.get(FeatureFlagId.ShowRiskProfileAttackSurfaceRiskLevels);
  }

  set showRiskProfileAttackSurfaceRiskLevels(value: boolean) {
    this.set(FeatureFlagId.ShowRiskProfileAttackSurfaceRiskLevels, value);
  }

  get hideEnvironmentIndicator() {
    return this.get(FeatureFlagId.HideEnvironmentIndicator);
  }

  set hideEnvironmentIndicator(value: boolean) {
    this.set(FeatureFlagId.HideEnvironmentIndicator, value);
  }

  get showClearedAtColumnOnUnderwritingQueue() {
    return this.get(FeatureFlagId.ShowClearedAtColumnOnUnderwritingQueue);
  }

  set showClearedAtColumnOnUnderwritingQueue(value: boolean) {
    this.set(FeatureFlagId.ShowClearedAtColumnOnUnderwritingQueue, value);
  }

  get enableNewConvergeBrand() {
    return this.get(FeatureFlagId.EnableNewConvergeBrand);
  }

  set enableNewConvergeBrand(value: boolean) {
    this.set(FeatureFlagId.EnableNewConvergeBrand, value);
  }

  get enableSearchV1() {
    return this.get(FeatureFlagId.EnableSearchV1);
  }

  set enableSearchV1(value: boolean) {
    this.set(FeatureFlagId.EnableSearchV1, value);
  }

  get includeNonSubmissionResultsInSearch() {
    return this.get(FeatureFlagId.IncludeNonSubmissionResultsInSearch);
  }

  set includeNonSubmissionResultsInSearch(value: boolean) {
    this.set(FeatureFlagId.IncludeNonSubmissionResultsInSearch, value);
  }

  get showRaterUpdateNotifications() {
    return this.get(FeatureFlagId.ShowRaterUpdateNotifications);
  }

  set showRaterUpdateNotifications(value: boolean) {
    this.set(FeatureFlagId.ShowRaterUpdateNotifications, value);
  }

  get enableAddConvergeToExcessTowerPrompt() {
    return this.get(FeatureFlagId.EnableAddConvergeToExcessTowerPrompt);
  }

  set enableAddConvergeToExcessTowerPrompt(value: boolean) {
    this.set(FeatureFlagId.EnableAddConvergeToExcessTowerPrompt, value);
  }

  get mockEndorsementQuestions() {
    return this.get(FeatureFlagId.MockEndorsementQuestions);
  }

  set mockEndorsementQuestions(value: boolean) {
    this.set(FeatureFlagId.MockEndorsementQuestions, value);
  }

  get enableRaterStateIndicator() {
    return this.get(FeatureFlagId.EnableRaterStateIndicator);
  }

  set enableRaterStateIndicator(value: boolean) {
    this.set(FeatureFlagId.EnableRaterStateIndicator, value);
  }

  get enableRaterExcessLayerParticipantReplacement() {
    return this.get(FeatureFlagId.EnableRaterExcessLayerParticipantReplacement);
  }

  set enableRaterExcessLayerParticipantReplacement(value: boolean) {
    this.set(FeatureFlagId.EnableRaterExcessLayerParticipantReplacement, value);
  }

  get enableRaterQueryValidation() {
    return this.get(FeatureFlagId.EnableRaterQueryValidation);
  }

  set enableRaterQueryValidation(value: boolean) {
    this.set(FeatureFlagId.EnableRaterQueryValidation, value);
  }

  get enableRaterMessageValidation() {
    return this.get(FeatureFlagId.EnableRaterMessageValidation);
  }

  set enableRaterMessageValidation(value: boolean) {
    this.set(FeatureFlagId.EnableRaterMessageValidation, value);
  }

  get enablePartialRaterSaves() {
    return this.get(FeatureFlagId.EnablePartialRaterSaves);
  }

  set enablePartialRaterSaves(value: boolean) {
    this.set(FeatureFlagId.EnablePartialRaterSaves, value);
  }
}
